import { Button } from '@/components/ui/button';
import TabErrorBanner from '@/components/v2/molecules/admin/shared/TabErrorBanner';
import BookingsTabSkeleton from '@/components/v2/molecules/admin/tabs/AdminBookingsTab/BookingsTabSkeleton';
import TablePagination from '@/components/v2/molecules/admin/shared/TablePagination';
import { useBookingsFiltered } from '@/hooks/admin/tabs/useBookingsFiltered';
import { useToast } from '@/hooks/use-toast';
import {
  Booking,
  useBookings,
  useUpdateBookingStatus,
} from '@/hooks/useBookings';
import { useOrigins } from '@/hooks/useOrigins';
import { useProperties } from '@/hooks/useProperties';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useContracts } from '@/hooks/useContracts';
import { parseDateFromDatabase } from '@/utils/dateParser';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Download, Plus } from 'lucide-react';
import { useState } from 'react';
import BookingFormModal from '../../../../../BookingFormModal';
import BookingsEmptyBanner from '../../../../molecules/admin/tabs/AdminBookingsTab/BookingsEmptyBanner';
import BookingsFilteredBanner from '../../../../molecules/admin/tabs/AdminBookingsTab/BookingsFilteredBanner';
import BookingsFilters from './BookingsFilters';
import BookingsTable from './BookingsTable';
import GuestFormModal from '../AdminGuestsTab/GuestFormModal';
import { Guest } from '@/hooks/useGuests';
import AdminPageHeader from '@/components/v2/atoms/admin/layout/AdminPageHeader';

const AdminBookingsTab = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

  // NUEVOS estados para creación de huésped
  const [showGuestModal, setShowGuestModal] = useState(false);
  const [newGuestName, setNewGuestName] = useState('');
  const [pendingOriginId, setPendingOriginId] = useState('');
  const [pendingGuestCallback, setPendingGuestCallback] = useState<
    ((guest: Guest) => void) | null
  >(null);

  const { data: allBookings = [] } = useBookings();
  const { data: properties = [] } = useProperties();
  const { data: origins = [] } = useOrigins();
  const { data: organizations = [] } = useOrganizations();
  const { data: contracts = [] } = useContracts();

  const updateStatusMutation = useUpdateBookingStatus();

  const {
    bookings,
    totalCount,
    totalPages,
    isLoading: isLoadingBookings,
    error: errorBookings,
    refetch: refetchBookings,
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    guestSearch,
    selectedProperties,
    selectedOrganizations,
    selectedOrigin,
    selectedStatus,
    dateFrom,
    dateTo,
    hasActiveFilters,
    handleGuestSearchChange,
    handlePropertiesChange,
    handleOrganizationsChange,
    handleOriginChange,
    handleStatusChange,
    handleDateFromChange,
    handleDateToChange,
    resetFilters,
  } = useBookingsFiltered();

  const { toast } = useToast();

  const formatBookingDate = (dateString: string) => {
    const date = parseDateFromDatabase(dateString);
    if (!date) return dateString;

    return format(date, 'dd/MM/yy', { locale: es });
  };

  const handleNewBooking = () => {
    setSelectedBooking(null);
    setShowModal(true);
  };

  const handleEditBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowModal(true);
  };

  const handleCancelBooking = (booking: Booking) => {
    if (
      window.confirm('¿Estás seguro de que quieres cancelar esta reservación?')
    ) {
      updateStatusMutation.mutate({ id: booking.id, status: 'Cancelada' });
    }
  };

  const handleActivateBooking = (booking: Booking) => {
    updateStatusMutation.mutate({ id: booking.id, status: 'Reservada' });
  };

  const handleCopyUrl = async (bookingId: string) => {
    const url = `${window.location.origin}/${bookingId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: 'URL copiada',
        description:
          'El enlace de la reservación ha sido copiado al portapapeles',
      });
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast({
        title: 'Error',
        description: 'No se pudo copiar el enlace',
        variant: 'destructive',
      });
    }
  };

  const handleDownloadCSV = () => {
    const csvHeaders = [
      'Propiedad',
      'Origen',
      'Huésped',
      'Llegada',
      'Salida',
      'Noches',
      'Pago Estimado',
      'Hospedaje Realizado',
      'Pago Realizado',
    ];

    const csvData = bookings.map((booking) => [
      booking.property_short_name,
      booking.origin_name || 'N/A',
      booking.guest_name,
      formatBookingDate(booking.check_in),
      formatBookingDate(booking.check_out),
      booking.nights.toString(),
      (booking.estimated_payout ?? 0).toString(),
      booking.stay_completed ? 'Si' : 'No',
      booking.paid ? 'Si' : 'No',
    ]);

    const csvContent = [
      csvHeaders.join(','),
      ...csvData.map((row) => row.map((field) => `"${field}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `reservaciones_${new Date().toISOString().split('T')[0]}.csv`,
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: 'CSV descargado',
      description: `Se han exportado ${bookings.length} reservaciones`,
    });
  };

  if (isLoadingBookings) {
    return <BookingsTabSkeleton />;
  }

  if (errorBookings) {
    return (
      <TabErrorBanner
        title="Error al cargar reservaciones"
        message="No se pudieron cargar las reservaciones. Por favor, intenta nuevamente."
        onRetry={() => refetchBookings()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <AdminPageHeader title="Reservaciones">
          <div className="flex gap-2">
            <Button
              onClick={handleDownloadCSV}
              variant="outline"
              disabled={bookings.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Descargar CSV
            </Button>

            <Button
              className="bg-brand-primary hover:bg-brand-primary-hover"
              onClick={handleNewBooking}
            >
              <Plus className="h-4 w-4 mr-2" />
              Nueva Reservación
            </Button>
          </div>
        </AdminPageHeader>

        <BookingsFilters
          guestSearch={guestSearch}
          selectedProperties={selectedProperties}
          selectedOrganizations={selectedOrganizations}
          selectedOrigin={selectedOrigin}
          checkInDate={dateFrom}
          checkOutDate={dateTo}
          setGuestSearch={handleGuestSearchChange}
          setSelectedProperties={handlePropertiesChange}
          setSelectedOrganizations={handleOrganizationsChange}
          setSelectedOrigin={handleOriginChange}
          setCheckInDate={handleDateFromChange}
          setCheckOutDate={handleDateToChange}
          properties={properties}
          organizations={organizations}
          contracts={contracts}
          origins={origins}
          hasActiveFilters={hasActiveFilters}
          clearAllFilters={resetFilters}
        />
      </div>

      {allBookings.length === 0 ? (
        <BookingsEmptyBanner onCreateBooking={handleNewBooking} />
      ) : bookings.length === 0 ? (
        <BookingsFilteredBanner onClearFilters={resetFilters} />
      ) : (
        <>
          <BookingsTable
            bookings={bookings}
            onEdit={handleEditBooking}
            onCopy={handleCopyUrl}
            onCancel={handleCancelBooking}
            onActivate={handleActivateBooking}
            isLoading={updateStatusMutation.isPending}
          />
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}

      <BookingFormModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        booking={selectedBooking}
        onBookingCreated={refetchBookings}
        origins={origins}
        onRequestGuestCreation={(guestName, originId) => {
          setShowModal(false);
          setNewGuestName(guestName);
          setPendingOriginId(originId || '');
          setShowGuestModal(true);
          setPendingGuestCallback(() => () => {
            setShowModal(true);
          });
        }}
      />

      <GuestFormModal
        isOpen={showGuestModal}
        onClose={() => {
          setShowGuestModal(false);
          setNewGuestName('');
          setPendingOriginId('');
          setPendingGuestCallback(null);
        }}
        guest={null}
        defaultName={newGuestName}
        defaultOriginId={pendingOriginId}
        onGuestCreated={(guest) => {
          setShowGuestModal(false);
          setNewGuestName('');
          setPendingOriginId('');
          pendingGuestCallback?.(guest);
        }}
      />
    </div>
  );
};

export default AdminBookingsTab;
